import { SignDTO } from "@api/client";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { useEffect, useState } from "react";
import AppButton from "shared/design-system/components/app-button";
import AppConfirm from "shared/design-system/components/app-confirm";
import AppNoResults from "shared/design-system/components/app-no-results";
import AppPage from "shared/design-system/components/app-page";
import AppPaginator from "shared/design-system/components/app-paginator";
import { AppSearchboxActiveFilter } from "shared/design-system/components/app-searchbox/types.models";
import AppTable from "shared/design-system/components/app-table";
import AppTableTh from "shared/design-system/components/app-table/app-table-th";
import { AppToastService } from "shared/design-system/components/app-toast/app-toast.service";
import {
	signAddFilter,
	signDelete,
	signDeleteFilter,
	signGetList,
	signSetTablePage,
	signSetTablePageSize,
	signSetTableSort,
} from "./_redux/actions";
import {
	signFiltersSelector,
	signListSelector,
	signTablePageSelector,
	signTablePageSizeSelector,
	signTableSortDirSelector,
	signTableSortSelector,
} from "./_redux/selectors";
import SignEditModal from "./signs-edit-modal";

const SignPage: React.FC = () => {
	const dispatch = useAppDispatch();

	const tablePage = useAppSelector(signTablePageSelector);
	const tablePageSize = useAppSelector(signTablePageSizeSelector);
	const tableSort = useAppSelector(signTableSortSelector);
	const tableSortDir = useAppSelector(signTableSortDirSelector);
	const filters = useAppSelector(signFiltersSelector);
	const response = useAppSelector(signListSelector);

	const [showEditForm, setShowEditForm] = useState<boolean | number>(false);
	const [itemDelete, setItemDelete] = useState<number>();

	const handleTablePageChange = (page: number) => {
		dispatch(signSetTablePage(page));
	};

	const handleTablePageSizeChange = (pageSize: number) => {
		dispatch(signSetTablePageSize(pageSize));
	};

	const handleTableSort = (property: keyof SignDTO) => {
		dispatch(signSetTableSort(property));
	};

	const handleAddFilter = (filter: AppSearchboxActiveFilter) => {
		dispatch(signAddFilter(filter));
	};

	const handleDeleteFilter = (filter: string) => {
		dispatch(signDeleteFilter(filter));
	};

	const handleNewClick = () => {
		setShowEditForm(true);
	};

	const handleEditClick = (id: number) => {
		setShowEditForm(id);
	};

	const handleNew = (reload?: boolean) => {
		if (reload) getData();

		setShowEditForm(false);
	};

	const handleDelete = (id: number) => {
		setItemDelete(id);
	};

	const handleConfirmDelete = () => {
		if (!itemDelete) return console.error("itemDelete not found");

		dispatch(signDelete(itemDelete)).then((res) => {
			if (res) {
				setItemDelete(undefined);
				AppToastService.success("Segno eliminato con successo");
				getData();
			}
		});
	};

	const getData = () => {
		dispatch(signGetList());
	};

	useEffect(() => {
		getData();
	}, [tablePage, tablePageSize, tableSort, tableSortDir, filters]);

	return (
		<>
			<AppPage
				title="Signs"
				subtitle="Aggiungi, modifica o cancella i segni dell'app Signs"
				actions={
					<AppButton
						onClick={() => handleNewClick()}
						className="w-full md:w-auto"
					>
						<i className="fas fa-plus me-2" />
						Aggiungi
					</AppButton>
				}
			>
				<div className="card mb-3">
					<div className="card-body">
						<AppTable>
							<thead>
								<tr>
									<AppTableTh>Titolo</AppTableTh>
									<AppTableTh>&nbsp;</AppTableTh>
								</tr>
							</thead>

							<tbody>
								{response?.items?.map((item, i) => (
									<tr key={`sign_${i}`}>
										<td>
											<b>{item?.title}</b>
										</td>
										<td width={300}>
											<AppButton
												size="sm"
												color="light"
												className="me-2"
												onClick={() => handleEditClick(item?.id!)}
											>
												<i className="fas fa-pencil" />
												Modifica
											</AppButton>

											<AppButton
												size="sm"
												color="danger"
												onClick={() => handleDelete(item.id!)}
											>
												<i className="fas fa-trash" />
												Cancella
											</AppButton>
										</td>
									</tr>
								))}
							</tbody>
						</AppTable>
					</div>
				</div>

				{response?.items?.length == 0 ? (
					<AppNoResults />
				) : (
					<div className="card">
						<div className="card-body">
							<AppPaginator
								currentPage={tablePage}
								pageSize={tablePageSize}
								totalElements={response?.count!}
								onPageChange={handleTablePageChange}
								onPageSizeChange={handleTablePageSizeChange}
							/>
						</div>
					</div>
				)}
			</AppPage>

			<SignEditModal
				show={!!showEditForm}
				onHide={handleNew}
				{...(typeof showEditForm == "number" ? { entId: showEditForm } : {})}
			/>

			<AppConfirm
				show={!!itemDelete}
				onConfirm={() => handleConfirmDelete()}
				onHide={() => setItemDelete(undefined)}
				title="Elimina Segno"
				message="Confermi di voler eleminilare il segno?"
			/>
		</>
	);
};
export default SignPage;
